import React, { useState, useRef, useEffect } from 'react';
import usePDA1090_R01Store from "./usePDA1090_R01Store";
import { usePDA1090_R01Content } from "./PDA1090_R01Content";

/**
 * PDA1090_R01 재고실사 입력 폼
 * PDA1080_R01 디자인 스타일 적용
 */
const PDA1090_R01Form = () => {
  const { 
    lotNo, 
    setLotNo, 
    data,
    selectedStock,
    actualStock,
    setActualStock,
    loading,
  } = usePDA1090_R01Store();

  const { 
    handleScanLot, 
    handleSaveAdjustment,
    handleLotNoEnter,
    handleActualStockEnter,
  } = usePDA1090_R01Content();
  
  // 키보드 제어
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);
  const lotInputRef = useRef<HTMLInputElement>(null);
  const actualStockInputRef = useRef<HTMLInputElement>(null);

  // 초기 포커스
  useEffect(() => {
    lotInputRef.current?.focus();
  }, []);

  // LOT 조회 후 실재고 필드로 포커스
  useEffect(() => {
    if (data && selectedStock) {
      actualStockInputRef.current?.focus();
    }
  }, [data, selectedStock]);

  // 더블탭으로 키보드 활성화
  const handleDoubleTap = () => {
    const now = Date.now();
    const timeDiff = now - lastTapTime;
    
    if (timeDiff < 300) {
      setKeyboardEnabled(true);
      lotInputRef.current?.focus();
    }
    
    setLastTapTime(now);
  };

  // 토글 버튼으로 키보드 제어
  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => lotInputRef.current?.focus(), 100);
    }
  };

  // Enter 키 처리 - LOT 스캔
  const handleLotNoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLotNoEnter();
    }
  };

  // Enter 키 처리 - 실재고 입력
  const handleActualStockKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleActualStockEnter();
    }
  };

  // 실재고 입력 핸들러
  const handleActualStockChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setActualStock(null);
    } else {
      setActualStock(Number(value));
    }
  };

  // 차이 계산
  const getDiffDisplay = () => {
    if (!selectedStock || actualStock === null) return '';
    const diff = actualStock - selectedStock.stockQty;
    if (diff === 0) return '';
    const sign = diff > 0 ? '+' : '';
    return `(${sign}${diff.toLocaleString()})`;
  };

  return (
    <div className="w-full bg-white p-4 pb-2 border-b border-gray-200">
      <div className="space-y-1">
        
        {/* LOT No */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">LOT No</label>
          <div className="flex-1 flex gap-1">
            <input
              ref={lotInputRef}
              type="text"
              value={lotNo}
              onChange={(e) => setLotNo(e.target.value)}
              onKeyDown={handleLotNoKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? 'text' : 'none'}
              disabled={loading}
              placeholder="LOT 번호 스캔"
              autoComplete="off"
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                keyboardEnabled
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title={keyboardEnabled ? '키보드 숨기기' : '키보드 표시'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </button>
          </div>
        </div>
        {/* 품목코드 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목코드</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={data?.itemCd || ''}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 품목명 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={data?.itemName || ''}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 규격 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">규격</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={data?.itemSpec || ''}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 단위 및 등급 (한 행에 배치, 각각 절반 너비) */}
        <div className="flex items-center gap-1">
          {/* 왼쪽 절반: 단위 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-20 flex-shrink-0">단위</label>
            <input
              type="text"
              value={data?.unit || ''}
              readOnly
              className="w-[50px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
          </div>
          {/* 오른쪽 절반: 등급 */}
          <div className="flex items-center gap-1">
            <label className="text-lg font-medium text-black w-10 flex-shrink-0">등급</label>
            <input
              type="text"
              value={selectedStock?.itemStatus ? `${selectedStock.itemStatus}` : ''}
              readOnly
              className="w-[50px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
          </div>
        </div>

        {/* 창고 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">창고</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={selectedStock?.warehouseNm || ''}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>


        {/* 현재고 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">현재고</label>
          <div className="flex-1 flex gap-1">
            <input
              type="text"
              value={selectedStock ? selectedStock.stockQty.toLocaleString() : ''}
              readOnly
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
            />
            <div className="w-10 flex-shrink-0"></div>
          </div>
        </div>

        {/* 실재고 및 저장 버튼 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">실재고</label>
          <div className="flex-1 flex gap-1 items-center">
            <input
              ref={actualStockInputRef}
              type="number"
              value={actualStock !== null ? actualStock : ''}
              onChange={handleActualStockChange}
              onKeyDown={handleActualStockKeyDown}
              disabled={loading || !data || !selectedStock}
              placeholder="수량"
              className="w-24 px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              min="0"
              step="1"
            />
            {/* 차이 표시 */}
            <span className={`text-lg font-medium ${
              actualStock !== null && selectedStock && actualStock !== selectedStock.stockQty
                ? actualStock > selectedStock.stockQty 
                  ? 'text-blue-600' 
                  : 'text-red-600'
                : 'text-gray-400'
            }`}>
              {getDiffDisplay()}
            </span>
            <button
              onClick={handleSaveAdjustment}
              disabled={loading || !data || !selectedStock || actualStock === null}
              className="flex-1 bg-blue-500 text-white px-2 rounded text-lg font-medium hover:bg-blue-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              저장
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PDA1090_R01Form;
